import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { of } from 'rxjs';
import { ScStepType } from '../models';

export const getIndexBy = (array: any[], { name, value }): number => {
  for (let i = 0; i < array.length; i++) {
    if (array[i][name] === value) {
      return i;
    }
  }
  return -1;
};

export const autocompleteFilter = (term: string, states: any[]) =>
  of(typeof term === 'string' ? states.filter(state => state.toLowerCase().indexOf(term.toLowerCase()) === 0) : states.slice());

export const convertFromCamelToPascal = (value: string) => {
  const result = value.replace(/([A-Z])/g, '$1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const getCurrentRouteSnapshot = (route: ActivatedRoute): ActivatedRouteSnapshot => {
  while (route.firstChild) {
    route = route.firstChild;
  }
  return route.snapshot;
};

const findReplaceString = (string, find, replace) => {
  if (/[a-zA-Z\_]+/g.test(string)) {
    return string.replace(new RegExp('{{(?:\\s+)?(' + find + ')(?:\\s+)?}}'), replace);
  } else {
    throw new Error('Find statement does not match regular expression: /[a-zA-Z_]+/');
  }
};

export const getInsideDoubleCurly = (str: string) =>
  str
    .split('{{')
    .filter((val: string) => val.includes('}}'))
    .map((val: string) => val.substring(0, val.indexOf('}}')));

export const interpolateString = (templateString: string, binding: any) => {
  const [content] = getInsideDoubleCurly(templateString);
  if (content) {
    return findReplaceString(templateString, content, pluck(binding, content));
  } else {
    return templateString;
  }
};

export const pluck = (x: any, prop: string): string => {
  const properties = prop.split('.');
  const length = properties.length;
  if (length === 0) {
    throw new Error('list of properties cannot be empty.');
  }
  let currentProp: any = x;
  for (let i = 0; i < length; i++) {
    const p = currentProp[properties[i].trim()];
    if (typeof p !== 'undefined') {
      currentProp = p;
    } else {
      return undefined;
    }
  }
  return currentProp;
};

export const generateID = () => {
  const min = Math.ceil(1);
  const max = Math.floor(10000000);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const groupBy = (arr: any[], property: string) =>
  arr.reduce((memo, x) => {
    if (!memo[x[property]]) {
      memo[x[property]] = [];
    }
    memo[x[property]].push(x);
    return memo;
  }, {});

// const exampleConfig = [
//   {
//     key: 'key-1',
//     name: 'key-1',
//     type: 'input'
//   },
//   {
//     key: 'key-3',
//     fieldGroupClassName: 'container-name',
//     fieldGroup: [
//       {
//         type: 'select',
//         key: 'key-3',
//         name: 'key-3'
//       }
//     ]
//   }
// ];

export const flattenFormlyFieldConfig = ({ key, type, fieldGroup = [] }): { key: string; type: string; value: string }[] =>
  fieldGroup
    .reduce((r, o) => [...r, ...flattenFormlyFieldConfig(o)], [
      {
        key,
        type,
        value: ''
      }
    ])
    .filter(obj => !!obj && !!obj.key && !!obj.type && obj.type !== 'template');

// flattenFormlyFieldConfig(exampleConfig);

// const steps = [
//   {
//     label: 'Step 1',
//     icon: 'icon-name',
//     fields: exampleConfig
//   }
// ];
export const generateStepperFields = (stepperConfig: ScStepType[]) =>
  stepperConfig.map((c: ScStepType, index: number) => ({
    step: index,
    containers: c.fields
      .map((f: FormlyFieldConfig) => {
        const formFields = flattenFormlyFieldConfig(f as any);
        return {
          parentContainer: f.fieldGroupClassName,
          fields: formFields
        };
      })
      .filter(({ fields }) => fields && fields.length)
  }));

// generateStepperFields(steps)

export class DeepDiffMapper {
  private _updates = {
    VALUE_CREATED: 'created',
    VALUE_UPDATED: 'updated',
    VALUE_DELETED: 'deleted',
    VALUE_UNCHANGED: 'unchanged'
  };

  public map(obj1: any, obj2: any) {
    if (this._isFunction(obj1) || this._isFunction(obj2)) {
      throw new Error('Invalid argument. Function given, object expected.');
    }
    if (this._isValue(obj1) || this._isValue(obj2)) {
      return {
        type: this._compareValues(obj1, obj2),
        data: obj1 === undefined ? obj2 : obj1
      };
    }

    const diff = {};
    for (const key in obj1) {
      if (this._isFunction(obj1[key])) {
        continue;
      }

      let value2: any;
      if (obj2[key] !== undefined) {
        value2 = obj2[key];
      }

      diff[key] = this.map(obj1[key], value2);
    }
    for (const key in obj2) {
      if (this._isFunction(obj2[key]) || diff[key] !== undefined) {
        continue;
      }

      diff[key] = this.map(undefined, obj2[key]);
    }

    return diff;
  }

  private _compareValues(value1: any, value2: any) {
    if (value1 === value2) {
      return this._updates.VALUE_UNCHANGED;
    }
    if (this._isDate(value1) && this._isDate(value2) && value1.getTime() === value2.getTime()) {
      return this._updates.VALUE_UNCHANGED;
    }
    if (value1 === undefined) {
      return this._updates.VALUE_CREATED;
    }
    if (value2 === undefined) {
      return this._updates.VALUE_DELETED;
    }
    return this._updates.VALUE_UPDATED;
  }

  private _isFunction(x: any) {
    return Object.prototype.toString.call(x) === '[object Function]';
  }
  private _isArray(x: any) {
    return Object.prototype.toString.call(x) === '[object Array]';
  }
  private _isDate(x: any) {
    return Object.prototype.toString.call(x) === '[object Date]';
  }
  private _isObject(x: any) {
    return Object.prototype.toString.call(x) === '[object Object]';
  }
  private _isValue(x: any) {
    return !this._isObject(x) && !this._isArray(x);
  }
}

export const deepDiffMapper = (function() {
  return {
    VALUE_CREATED: 'created',
    VALUE_UPDATED: 'updated',
    VALUE_DELETED: 'deleted',
    VALUE_UNCHANGED: 'unchanged',
    map(obj1, obj2, parent?, updatedKey?) {
      if (this.isFunction(obj1) || this.isFunction(obj2)) {
        throw new Error('Invalid argument. Function given, object expected.');
      }
      if (this.isValue(obj1) || this.isValue(obj2)) {
        return {
          type: this.compareValues(obj1, obj2),
          value: obj1 === undefined ? obj2 : obj1,
          from: parent,
          key: updatedKey
        };
      }

      const diff = {};
      for (const key in obj1) {
        if (this.isFunction(obj1[key])) {
          continue;
        }

        let value2;
        if (obj2[key] !== undefined) {
          value2 = obj2[key];
        }

        diff[key] = this.map(obj1[key], value2, obj1, key);
      }
      for (const key in obj2) {
        if (this.isFunction(obj2[key]) || diff[key] !== undefined) {
          continue;
        }

        diff[key] = this.map(undefined, obj2[key], obj2, key);
      }

      return diff;
    },
    compareValues(value1, value2) {
      if (value1 === value2) {
        return this.VALUE_UNCHANGED;
      }
      if (this.isDate(value1) && this.isDate(value2) && value1.getTime() === value2.getTime()) {
        return this.VALUE_UNCHANGED;
      }
      if (value1 === undefined) {
        return this.VALUE_CREATED;
      }
      if (value2 === undefined) {
        return this.VALUE_DELETED;
      }
      return this.VALUE_UPDATED;
    },
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    isFunction(x: any) {
      return Object.prototype.toString.call(x) === '[object Function]';
    },
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    isArray(x: any) {
      return Object.prototype.toString.call(x) === '[object Array]';
    },
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    isDate(x: any) {
      return Object.prototype.toString.call(x) === '[object Date]';
    },
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    isObject(x: any) {
      return Object.prototype.toString.call(x) === '[object Object]';
    },
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    isValue(x: any) {
      return !this.isObject(x) && !this.isArray(x);
    }
  };
})();

export function evalFieldExpression(expression: string): any {
  return Function('model', 'formState', 'field', `var result;${expression}; if (result) return result;`).bind(this);
}
// Example:
// expressionProperties: {
//   'templateOptions.datepickerOptions.min': evalFieldExpression.call(this, `
//      const annexId = this.store.selectSnapshot(store => store.policy.selectedAnnexId);
//      const list = this.store.selectSnapshot(store => store.policy.annexesList);
//      const selectedAnnex = list.find(item => item.id === annexId);
//      if (selectedAnnex) {
//       result = selectedAnnex.beginDate;
//      }
//   `),
//   'templateOptions.datepickerOptions.max': evalFieldExpression.call(this, `result = model.validTo`)
// }

export const dateDiff = {
  inDays: (d1: Date, d2: Date) => {
    const t2 = d2.getTime();
    const t1 = d1.getTime();
    const diffTime = Math.abs(t2 - t1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  },
  inMonths: (d1: Date, d2: Date) => {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
  },
  inYears: (d1: Date, d2: Date) => d2.getFullYear() - d1.getFullYear()
};
